import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Download, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import EmptyState from '../../components/crm/EmptyState';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import BackLink from '../../components/admin/BackLink';
import { SEVERITY_TONE } from '../../lib/audits';

export default function AuditDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rerunning, setRerunning] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/api/admin/audits/${id}`);
      setAudit(res.audit);
    } catch (err) {
      setError(err?.message || 'Failed to load audit');
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const rerun = async () => {
    setRerunning(true);
    try {
      const res = await api.post(`/api/admin/audits/${id}/rerun`);
      if (res?.audit?.id) {
        window.location.assign(`/admin/audits/${res.audit.id}`);
      }
    } catch (err) {
      alert(err?.message || 'Rerun failed');
    } finally {
      setRerunning(false);
    }
  };

  const downloadPdf = () => {
    window.open(`/api/admin/audits/${id}/pdf`, '_blank');
  };

  const deleteAudit = async () => {
    setDeleting(true);
    try {
      await api.delete(`/api/admin/audits/${id}`);
      // Bounce back to the account's audits tab if we have one, otherwise
      // the audits index. accountId comes from the current row.
      if (audit?.accountId) {
        navigate(`/admin/accounts/${audit.accountId}?tab=audits`);
      } else {
        navigate('/admin/audits');
      }
    } catch (err) {
      alert(err?.message || 'Delete failed');
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  if (user?.role !== 'nexsav_admin' && user?.role !== 'ops') {
    return <EmptyState title="Staff only" />;
  }

  // Try to parse the violations payload — Sprint F populates axeViolationsJson;
  // pre-Sprint-F audits used rawResultsJson with the same shape.
  let violations = [];
  try {
    const json = audit?.axeViolationsJson || audit?.rawResultsJson;
    const raw = json ? JSON.parse(json) : null;
    if (raw?.violations) violations = raw.violations;
  } catch { /* stub payload or malformed — skip */ }

  const bySeverity = { critical: [], serious: [], moderate: [], minor: [] };
  for (const v of violations) {
    const sev = v.impact || 'minor';
    if (bySeverity[sev]) bySeverity[sev].push(v);
  }

  return (
    <div className="max-w-4xl mx-auto">
      <BackLink to="/admin/audits" className="inline-flex items-center gap-1.5 text-[12px] text-gray-500 hover:text-white mb-4">
        <ArrowLeft className="w-3 h-3" /> Back
      </BackLink>
      {loading && !audit && <div className="animate-pulse h-20 rounded-xl bg-white/[0.03]" />}
      {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">{error}</div>}
      {audit && (
        <>
          <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
            <div className="min-w-0">
              <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight font-mono truncate">{audit.auditedUrl}</h1>
              <div className="text-[12px] text-gray-500 mt-1">
                Audited {audit.producedAt} · {audit.producedBy} · Account {String(audit.accountId).slice(0, 8)}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={downloadPdf} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] text-gray-300 hover:text-white bg-white/[0.03] border border-white/[0.08]">
                <Download className="w-3 h-3" /> PDF
              </button>
              <a
                href={`/api/admin/audits/${audit.id}/pdf?html=1`}
                target="_blank"
                rel="noopener noreferrer"
                title="Preview the report layout in HTML (skips R2 cache and Browser Rendering, useful locally and after template changes)"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] text-gray-300 hover:text-white bg-white/[0.03] border border-white/[0.08]"
              >
                Preview HTML
              </a>
              <button onClick={rerun} disabled={rerunning} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-50">
                <RefreshCw className={`w-3 h-3 ${rerunning ? 'animate-spin' : ''}`} />
                {rerunning ? 'Rerunning…' : 'Rerun'}
              </button>
              <button
                onClick={() => setConfirmDelete(true)}
                disabled={deleting}
                aria-label="Delete audit"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 disabled:opacity-50"
              >
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>
          </div>

          <ConfirmDialog
            open={confirmDelete}
            onClose={() => setConfirmDelete(false)}
            onConfirm={deleteAudit}
            title="Delete this audit?"
            description="Removes the audit row and history entry. The PDF in storage is left in place. This cannot be undone."
            confirmLabel={deleting ? 'Deleting…' : 'Delete audit'}
            destructive
          />

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-6">
            <div data-testid="audit-score-card" className="col-span-1 rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5 flex flex-col items-center justify-center">
              <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Lighthouse</div>
              <LighthouseScore score={audit.lighthouseScore ?? audit.score} />
            </div>
            <Stat label="Critical" value={audit.violationsCritical ?? 0} tone={SEVERITY_TONE.critical} />
            <Stat label="Serious"  value={audit.violationsSerious  ?? 0} tone={SEVERITY_TONE.serious} />
            <Stat label="Moderate" value={audit.violationsModerate ?? 0} tone={SEVERITY_TONE.moderate} />
            <Stat label="Minor"    value={audit.violationsMinor    ?? 0} tone={SEVERITY_TONE.minor} />
          </div>

          {audit.previousAuditId && (
            <PreviousAuditComparison currentId={audit.id} previousId={audit.previousAuditId} currentScore={audit.lighthouseScore ?? audit.score} currentViolations={violations} />
          )}

          <ToolsBanner />

          <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5">
            <h3 className="text-[13px] font-semibold text-white mb-3">axe-core findings</h3>
            {violations.length === 0 ? (
              <div className="text-[12px] text-gray-500">
                No violation drill-down available (raw axe-core output not populated; deploy-day real mode writes full results).
              </div>
            ) : (
              ['critical', 'serious', 'moderate', 'minor'].map((sev) => (
                bySeverity[sev].length > 0 && (
                  <div key={sev} className="mb-4 last:mb-0">
                    <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">{sev} ({bySeverity[sev].length})</div>
                    <div className="space-y-2">
                      {bySeverity[sev].map((v, i) => (
                        <div key={`${sev}-${i}`} className="p-3 rounded-lg border border-white/[0.05] bg-white/[0.01]">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className="text-[13px] text-white">{v.id || 'rule'}</div>
                              <div className="text-[11px] text-gray-500 mt-0.5">{v.description || v.help || ''}</div>
                              <div className="text-[11px] text-gray-600 mt-1">{(v.nodes || []).length} affected node{(v.nodes || []).length === 1 ? '' : 's'}</div>
                            </div>
                            {v.helpUrl && (
                              <a href={v.helpUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#B0FC38] hover:text-white shrink-0">
                                docs →
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              ))
            )}
          </div>

          <WaveSection audit={audit} />
        </>
      )}
    </div>
  );
}

function ToolsBanner() {
  return (
    <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-4 mb-3" data-testid="audit-tools-banner">
      <div className="text-[10px] uppercase tracking-[0.08em] text-gray-500 mb-3">Tools &amp; methodology</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
        <div className="rounded-lg p-3 flex flex-col items-center justify-center text-center" style={{ background: '#22577A', minHeight: 96 }}>
          <div className="text-[9px] uppercase tracking-[0.1em] text-[#A6E1FA]">Engine</div>
          <div className="font-bold text-[16px] mt-0.5 leading-tight tracking-wide" style={{ color: '#FFC857' }}>
            WAVE
          </div>
          <div className="text-[10px] text-[#A6E1FA] mt-0.5">by WebAIM</div>
        </div>
        <div className="rounded-lg p-3 flex flex-col items-center justify-center text-center" style={{ background: '#1a2332', minHeight: 96 }}>
          <div className="text-[9px] uppercase tracking-[0.1em] text-gray-400">Engine</div>
          <div className="font-bold text-[16px] mt-0.5 leading-tight">
            <span style={{ color: '#00C39A' }}>axe</span><span className="text-white">-core</span>
          </div>
          <div className="text-[10px] text-gray-400 mt-0.5">by Deque Systems</div>
        </div>
        <div className="rounded-lg p-3 md:col-span-1" style={{ background: '#1A3D7A' }}>
          <div className="text-[9px] uppercase tracking-[0.1em] text-[#B6CCED]">Public methodology reference</div>
          <div className="text-[12px] text-white mt-1 leading-snug">
            NYC Public Schools publicly references WAVE/WebAIM as part of its school website accessibility review process.
          </div>
          <div className="text-[10px] text-[#B6CCED] mt-1.5 leading-snug">
            This report is independently prepared by NexSav LLC and is not affiliated with, endorsed by, or sponsored by NYC Public Schools or NYC DOE.
          </div>
        </div>
      </div>
    </div>
  );
}

// WAVE category palette mirrors the PDF section so the in-app and PDF
// experiences feel consistent.
const WAVE_CATEGORY_TONE = {
  error: 'border-red-500/30 bg-red-500/5 text-red-200',
  contrast: 'border-orange-500/30 bg-orange-500/5 text-orange-200',
  alert: 'border-yellow-500/30 bg-yellow-500/5 text-yellow-200',
};

function WaveSection({ audit }) {
  // Three render branches: skipped / failed / success.
  if (!audit) return null;
  if (audit.waveStatus == null) {
    return (
      <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5 mt-3" data-testid="audit-wave-skipped">
        <div className="flex items-center justify-between gap-3 mb-1">
          <h3 className="text-[13px] font-semibold text-white">WAVE findings <span className="text-[11px] font-normal text-gray-500">(WebAIM)</span></h3>
          <span className="text-[10px] uppercase tracking-wider text-gray-500">Not run</span>
        </div>
        <div className="text-[12px] text-gray-500">WAVE was not run for this audit. Re-running this audit will include WAVE results.</div>
      </div>
    );
  }
  if (audit.waveStatus === 'failed') {
    return (
      <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5 mt-3" data-testid="audit-wave-failed">
        <div className="flex items-center justify-between gap-3 mb-1">
          <h3 className="text-[13px] font-semibold text-white">WAVE findings <span className="text-[11px] font-normal text-gray-500">(WebAIM)</span></h3>
          <span className="text-[10px] uppercase tracking-wider text-red-300">Failed</span>
        </div>
        <div className="text-[12px] text-red-300">WAVE evaluation failed: {audit.waveErrorMessage || 'unknown error'}. Re-run the audit to retry.</div>
      </div>
    );
  }

  let parsed = null;
  try { parsed = audit.waveResultsJson ? JSON.parse(audit.waveResultsJson) : null; } catch {}
  const cats = parsed?.categories || {};
  const stats = parsed?.statistics || {};
  const totalActionable = (audit.waveErrorCount || 0) + (audit.waveContrastErrorCount || 0) + (audit.waveAlertCount || 0);

  return (
    <div className="rounded-2xl border border-white/[0.05] bg-white/[0.015] p-5 mt-3" data-testid="audit-wave-section">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-[13px] font-semibold text-white">
          WAVE findings <span className="text-[11px] font-normal text-gray-500">(WebAIM)</span>
        </h3>
        <div className="text-[11px] text-gray-500">
          {totalActionable} actionable · {stats.totalelements != null ? `${stats.totalelements} elements` : '—'}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3">
          <div className="text-[10px] uppercase tracking-wider text-red-300">Errors</div>
          <div className="text-[20px] font-semibold text-red-200">{audit.waveErrorCount ?? 0}</div>
        </div>
        <div className="rounded-lg border border-orange-500/30 bg-orange-500/5 p-3">
          <div className="text-[10px] uppercase tracking-wider text-orange-300">Contrast errors</div>
          <div className="text-[20px] font-semibold text-orange-200">{audit.waveContrastErrorCount ?? 0}</div>
        </div>
        <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3">
          <div className="text-[10px] uppercase tracking-wider text-yellow-300">Alerts</div>
          <div className="text-[20px] font-semibold text-yellow-200">{audit.waveAlertCount ?? 0}</div>
        </div>
      </div>

      {['error', 'contrast', 'alert'].map((kind) => {
        const cat = cats[kind];
        const items = cat?.items ? Object.entries(cat.items) : [];
        const label = kind === 'error' ? 'Errors' : kind === 'contrast' ? 'Contrast errors' : 'Alerts';
        return (
          <div key={kind} className="mb-4 last:mb-0">
            <div className="flex items-center gap-2 mb-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${WAVE_CATEGORY_TONE[kind]}`}>
                {label}
              </span>
              <span className="text-[11px] text-gray-500">{cat?.count ?? 0}</span>
            </div>
            {items.length === 0 ? (
              <div className="text-[12px] text-gray-500 italic">No {label.toLowerCase()} found.</div>
            ) : (
              <div className="space-y-1.5">
                {items.map(([id, item]) => (
                  <div key={id} className="p-3 rounded-lg border border-white/[0.05] bg-white/[0.01] flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[12px] font-mono text-[#B0FC38]">{id}</div>
                      <div className="text-[12px] text-gray-300 mt-0.5">{item.description || id}</div>
                    </div>
                    <div className="text-[11px] text-gray-500 shrink-0">×{item.count || 0}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      <div className="mt-3 pt-3 border-t border-white/[0.05] text-[11px] text-gray-500">
        Positive: {cats.feature?.count ?? 0} features · {cats.structure?.count ?? 0} structural · {cats.aria?.count ?? 0} ARIA attributes
        {stats.waveurl && (
          <> · <a href={stats.waveurl} target="_blank" rel="noopener noreferrer" className="text-[#B0FC38] hover:text-white">live WAVE report ↗</a></>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className={`rounded-2xl border p-4 ${tone}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-[20px] font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function LighthouseScore({ score }) {
  if (score == null) return <div className="text-[24px] text-gray-500">—</div>;
  const tone = score >= 90 ? 'text-[#B0FC38]' : score >= 70 ? 'text-amber-300' : 'text-red-400';
  return (
    <div className={`text-[36px] font-bold ${tone}`} data-testid={`lighthouse-score-${score >= 90 ? 'green' : score >= 70 ? 'yellow' : 'red'}`}>
      {score}
      <span className="text-[12px] text-gray-500 font-normal ml-1">/100</span>
    </div>
  );
}

function PreviousAuditComparison({ previousId, currentScore, currentViolations }) {
  const [prev, setPrev] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/api/admin/audits/${previousId}`)
      .then((d) => { if (alive) setPrev(d.audit); })
      .catch(() => {});
    return () => { alive = false; };
  }, [previousId]);
  if (!prev) return null;

  const prevScore = prev.lighthouseScore ?? prev.score ?? 0;
  const delta = (currentScore || 0) - prevScore;
  const deltaTone = delta > 0 ? 'text-[#B0FC38]' : delta < 0 ? 'text-red-300' : 'text-gray-400';

  let prevViolations = [];
  try {
    const raw = prev.axeViolationsJson || prev.rawResultsJson;
    const j = raw ? JSON.parse(raw) : null;
    if (j?.violations) prevViolations = j.violations;
  } catch {}

  const idsCurrent = new Set(currentViolations.map((v) => v.id));
  const idsPrev = new Set(prevViolations.map((v) => v.id));
  const newIssues = [...idsCurrent].filter((id) => !idsPrev.has(id)).length;
  const fixedIssues = [...idsPrev].filter((id) => !idsCurrent.has(id)).length;
  const unchangedIssues = [...idsCurrent].filter((id) => idsPrev.has(id)).length;

  return (
    <div data-testid="audit-comparison-section" className="rounded-2xl border border-[#B0FC38]/30 bg-[#B0FC38]/[0.05] p-5 mb-6">
      <h3 className="text-[13px] font-semibold text-white mb-2">Compared to previous audit</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[13px]">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Score delta</div>
          <div className={`text-lg font-semibold ${deltaTone}`} data-testid="audit-comparison-delta">
            {delta >= 0 ? '+' : ''}{delta} ({prevScore} → {currentScore || 0})
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500">New issues</div>
          <div className="text-lg font-semibold text-amber-300" data-testid="audit-comparison-new">{newIssues}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Fixed issues</div>
          <div className="text-lg font-semibold text-[#B0FC38]" data-testid="audit-comparison-fixed">{fixedIssues}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Unchanged</div>
          <div className="text-lg font-semibold text-gray-300" data-testid="audit-comparison-unchanged">{unchangedIssues}</div>
        </div>
      </div>
    </div>
  );
}
